import type { Run, JiraBug, ActiveBuild } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Page-load requests in flight; drives the top loading bar. Background polls pass quiet.
let inflight = 0;
const listeners = new Set<() => void>();
const bump = (d: number) => { inflight += d; listeners.forEach(f => f()); };
export const subscribeLoading = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };
export const isFetching = () => inflight > 0;
async function get<T>(path: string, quiet = false): Promise<T> {
  if (!quiet) bump(1);
  try {
    const res = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
    return (await res.json()) as T;
  } finally {
    if (!quiet) bump(-1);
  }
}

// Stale-while-revalidate: a cached value is returned immediately and, once older than freshMs, refreshed quietly in the background.
// Concurrent callers for the same key share one request, so pages (and App) asking for the same data don't refetch it.
const cache = new Map<string, { data: unknown; at: number }>();
const pending = new Map<string, Promise<unknown>>();
function cached<T>(key: string, load: (quiet: boolean) => Promise<T>, freshMs = 30_000): Promise<T> {
  const start = (quiet: boolean): Promise<T> => {
    let p = pending.get(key) as Promise<T> | undefined;
    if (!p) {
      p = load(quiet)
        .then(data => { cache.set(key, { data, at: Date.now() }); return data; })
        .finally(() => { pending.delete(key); });
      pending.set(key, p);
    }
    return p;
  };
  const hit = cache.get(key);
  if (!hit) return start(false);
  if (Date.now() - hit.at > freshMs) start(true).catch(() => undefined);
  return Promise.resolve(hit.data as T);
}

/** Fetch all runs (list view: scenarios omit test case text). Backend filters by date server-side; client filters further. */
export function fetchRuns(): Promise<Run[]> {
  return cached('runs', quiet => get<Run[]>('/runs', quiet));
}

/** Fetch a single run with all scenarios, including test case text. */
export function fetchRun(runId: string): Promise<Run> {
  return cached(`run:${runId}`, quiet => get<Run>(`/runs/${runId}`, quiet));
}

/** Fetch all ai-expo-bug Jira issues. */
export function fetchBugs(): Promise<JiraBug[]> {
  return cached('bugs', quiet => get<JiraBug[]>('/bugs', quiet));
}

/** Trigger a Buildkite build. Returns the new ActiveBuild with status "scheduled". */
export async function triggerBuild(params: {
  sourceDb: string;
  destDb: string;
  triggeredBy: string;
  customTestRequests?: string;
}): Promise<ActiveBuild> {
  const res = await fetch(`${API_URL}/builds/trigger`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(msg || `API ${res.status}`);
  }
  return res.json() as Promise<ActiveBuild>;
}

/** Trigger an onboarding build for a new connector. Returns the new ActiveBuild with status "scheduled". */
export async function onboardConnector(params: {
  sourceName: string;
  connectorType: string;
  destination?: string;
  triggeredBy: string;
}): Promise<ActiveBuild> {
  const res = await fetch(`${API_URL}/builds/onboard`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(msg || `API ${res.status}`);
  }
  return res.json() as Promise<ActiveBuild>;
}

/** Fetch currently active builds (reads sheet + refreshes from Buildkite). */
export async function fetchActiveBuilds(): Promise<ActiveBuild[]> {
  return get<ActiveBuild[]>('/builds/active', true);
}
